import { notNull } from "./helpers";
import {
  AdditionalResponseData,
  Aspect,
  ChatGPTMessage,
  Response,
  ResponseWithRev,
  SurveyData,
  SurveyDataSingleLanguage,
  SurveyRevision,
  WelcomeAndDisclosure,
} from "./types";
import type { RecruitmentAudiencePlanId } from "@/backend/db/drizzle/types/idTypes";
import { getConjointTotalTasks } from "@/components/interview/conjoint";
import { getGaborGrangerEstimatedRungs } from "@/components/interview/gaborGranger";
import { getMaxDiffTotalScreens } from "@/components/interview/maxdiff";
import {
  format_message,
  sampleSurveyData,
} from "@/components/interview/message_tools";
import {
  Concept,
  ConceptId,
  ConditionalDef,
  ConditionalDefSingle,
  InterviewBlock,
  InterviewBlockId,
  InterviewItem,
  InterviewItemId,
  InterviewItemTemplate,
  InterviewItemTemplateId,
  MediaDef,
  QAState,
  QuestionItem,
  ScreenerQuestionRule,
  TaskConfig,
} from "@/components/interview/types";
import { OptionsResolverUtils } from "@/components/interview/utils/optionsResolverUtils";
import { TargetGroupId } from "@/components/outreach/panel/types";
import { isRecruitedParticipant } from "@/components/outreach/panel/utils";
import { NO_COLLECT_EMAIL_PARAM } from "@/components/responses/incentives/types";
import { SegmentId } from "@/components/responses/segments/types";
import { hasMinSelectedOptions } from "@/lib/interview/optionSelection";
import { assertNever } from "@/packages/ts-utils";

export const MATRIX_INTERVIEWER_INSTRUCTION =
  "The respondent sees the matrix rows and column options in their interface. Ask only the question text. Do not list, enumerate, or describe the rows or column options.";

export const CARD_SORT_INTERVIEWER_INSTRUCTION =
  "The respondent sees the cards and the groups in their interface and sorts each card into a group. Ask only the question text. Do not list, enumerate, or describe the cards or the groups.";

const hasOnlySkippedStates = (states: QAState[]) =>
  states.length > 0 && states.every((s) => s.skipped);

const passesConditionalSingle = ({
  conditional,
  searchParams,
  stateHistory,
  items,
  conceptId,
  target_group_id,
  audience_plan_id,
  segmentIds,
  itemIdsBeforeRestart,
  isPreview,
  shouldRequireAnsweredSource,
}: {
  conditional: ConditionalDefSingle;
  searchParams: { [key: string]: string };
  stateHistory: QAState[];
  items: InterviewItem[];
  conceptId: ConceptId | null;
  target_group_id: TargetGroupId | undefined;
  audience_plan_id: RecruitmentAudiencePlanId | undefined;
  segmentIds: SegmentId[] | null;
  itemIdsBeforeRestart?: Set<InterviewItemId>;
  isPreview?: boolean;
  shouldRequireAnsweredSource: boolean;
}) => {
  const { type } = conditional;
  switch (type) {
    case "searchParam": {
      const { parameter, value } = conditional;
      if (!parameter || !value) return true;
      return searchParams[parameter]?.trim() === value.trim();
    }
    case "answerString": {
      const { itemId, answer, matchingCriteria } = conditional;
      const states = stateHistory.filter((s) => s.currentItemId === itemId);
      if (
        shouldRequireAnsweredSource &&
        !states.some((s) => s.submittedAt || s.skipped)
      )
        return false;
      if (itemIdsBeforeRestart?.has(conditional.itemId) && states.length === 0)
        return true;
      if (hasOnlySkippedStates(states)) return true;
      const isOptionMatch = (s: QAState) => {
        const item = items.find((i) => i.id === s.currentItemId);
        if (!item) {
          console.error("getOptions: no item", s.currentItemId);
          return [];
        }
        const selectedOptionTexts =
          item.choices?.filter((o) => s.selectedOptionIds.includes(o.id)) ?? [];
        return selectedOptionTexts.some((o) => o.text.trim() === answer.trim());
      };
      const effectiveMatchingCriteria = matchingCriteria ?? "exact";
      switch (effectiveMatchingCriteria) {
        case "exact":
          return states.some(
            (s) => s.input.trim() === answer.trim() || isOptionMatch(s)
          );
        case "notEquals":
          return !states.some(
            (s) => s.input.trim() === answer.trim() || isOptionMatch(s)
          );
        case "contains":
          // here we use rawInput which combines text and options
          return states.some((s) => s.rawInput.trim().includes(answer.trim()));
        default:
          return assertNever(effectiveMatchingCriteria);
      }
    }
    case "selectedOptions":
    case "selectedTemplateOptions": {
      const { choices, matchingCriteria } = conditional;
      const states = stateHistory.filter((s) =>
        type === "selectedOptions"
          ? s.currentItemId === conditional.itemId
          : s.currentTemplateId === conditional.templateId &&
            (s.currentConceptId === null || s.currentConceptId === conceptId)
      );
      if (
        shouldRequireAnsweredSource &&
        !states.some((s) => s.submittedAt || s.skipped)
      )
        return false;
      if (hasOnlySkippedStates(states)) return true;
      if (itemIdsBeforeRestart && states.length === 0) {
        if (
          type === "selectedOptions" &&
          itemIdsBeforeRestart.has(conditional.itemId)
        )
          return true;
        if (type === "selectedTemplateOptions") {
          const matchingItem = items.find(
            (i) =>
              i.templateId === conditional.templateId &&
              (i.conceptId === null || i.conceptId === conceptId)
          );
          if (matchingItem && itemIdsBeforeRestart.has(matchingItem.id))
            return true;
        }
      }

      switch (matchingCriteria) {
        case "mustSelect":
          return states.some((s) => {
            const selected = new Set(s.selectedOptionIds);
            return choices.some((o) => selected.has(o));
          });
        case "mustSelectAtLeast":
          return (
            conditional.type === "selectedTemplateOptions" &&
            states.some((s) =>
              hasMinSelectedOptions({
                selectedOptionIds: new Set(s.selectedOptionIds),
                options: choices,
                minCount: conditional.minCount,
              })
            )
          );
        case "mustNotSelect":
          // need to use every because of follow up questions without selected options
          return states.every((s) => {
            const selected = new Set(s.selectedOptionIds);
            return choices.every((o) => !selected.has(o));
          });
        default:
          return assertNever(matchingCriteria);
      }
    }
    case "concept": {
      // TODO(soon): add concept selector
      // Note: will not be shown if this conditional is used outside of a concept block
      return conceptId !== null && conditional.concepts.includes(conceptId);
    }
    case "target_group": {
      // Legacy: retain exact target-group matching for existing study revisions.
      // target groups are not evaluated in previews so assume the condition passes
      if (isPreview) return true;
      return (
        !!target_group_id &&
        conditional.target_group_ids.includes(target_group_id)
      );
    }
    case "audience": {
      // TODO(soon): add audience selector in UI for preview
      if (isPreview) return true;
      return (
        notNull(audience_plan_id) &&
        conditional.audience_ids.includes(audience_plan_id)
      );
    }
    case "segment": {
      // TODO(soon): add segment selector
      // segments are not evaluated in previews so assume the condition passes
      if (isPreview) return true;

      // In edit-mode preview (itemIdsBeforeRestart defined), segment data is unavailable (segmentIds null),
      // so skip segment-based conditionals since we can't evaluate them.
      if (itemIdsBeforeRestart !== undefined && segmentIds === null)
        return true;
      return (
        segmentIds !== null &&
        segmentIds.some((id) => conditional.segmentIds.includes(id))
      );
    }
    default:
      assertNever(conditional);
  }
  // should never happen
  return true;
};

export const getBlockConditional = (
  block: InterviewBlock
): ConditionalDef | undefined => {
  switch (block.type) {
    case "flat":
    case "concept":
      return block.conditional;
    case "screening":
      return undefined;
    default:
      return assertNever(block);
  }
};

/**
 * Joins multiple conditional definitions into a single conjunction.
 */
export const combineConditionals = (
  ...parts: (ConditionalDef | undefined)[]
): ConditionalDef | undefined => {
  const present = parts.filter(notNull);
  if (present.length === 0) return undefined;
  if (present.length === 1) return present[0];
  return { type: "conjunction", items: present };
};

export const passesConditional = ({
  conditional,
  searchParams,
  stateHistory,
  items,
  additionalData,
  conceptId,
  itemIdsBeforeRestart,
  isPreview,
  shouldRequireAnsweredSource = false,
}: {
  conditional: ConditionalDef | undefined;
  searchParams: { [key: string]: string };
  stateHistory: QAState[];
  items: InterviewItem[];
  additionalData: AdditionalResponseData | undefined;
  conceptId: ConceptId | null;
  itemIdsBeforeRestart?: Set<InterviewItemId>;
  isPreview?: boolean;
  shouldRequireAnsweredSource?: boolean;
}) => {
  if (!conditional) return true;
  const searchParamsWithAdditional = {
    ...searchParams,
    ...(additionalData?.custom ?? {}),
  };

  const checkConditionalRecursive = (c: ConditionalDef): boolean => {
    if (c.type === "conjunction") {
      return c.items.every((c) => checkConditionalRecursive(c));
    } else if (c.type === "disjunction") {
      return c.items.some((c) => checkConditionalRecursive(c));
    } else {
      return passesConditionalSingle({
        conditional: c,
        searchParams: searchParamsWithAdditional,
        stateHistory,
        items,
        conceptId,
        target_group_id: additionalData?.targetGroupId,
        audience_plan_id: additionalData?.audiencePlanId,
        segmentIds: additionalData?.matchingSegmentIds ?? null,
        itemIdsBeforeRestart,
        isPreview,
        shouldRequireAnsweredSource,
      });
    }
  };

  return checkConditionalRecursive(conditional);
};

export const filterInterviewParts = ({
  items,
  searchParams,
  stateHistory,
  currentItemId,
  contextItems,
  additionalData,
  isEditMode = false,
  itemIdsBeforeRestart,
  isPreview,
}: {
  items: InterviewItem[];
  searchParams: { [key: string]: string };
  stateHistory: QAState[];
  currentItemId?: InterviewItemId; // always keep this
  // If provided, they are used for the filter check instead of items
  contextItems?: InterviewItem[];
  // additionalData: AdditionalResponseData | undefined;
  additionalData?: AdditionalResponseData;
  isEditMode?: boolean;
  itemIdsBeforeRestart?: Set<InterviewItemId>;
  isPreview?: boolean;
}) => {
  const filteredItems = items.filter((item) => {
    // Always keep current item
    if (item.id === currentItemId) return true;

    // Check conditional logic
    const passesCondition = passesConditional({
      conditional: item.conditional,
      searchParams,
      stateHistory,
      items: contextItems ?? items,
      additionalData,
      conceptId: item.conceptId,
      itemIdsBeforeRestart,
      isPreview,
    });

    if (!passesCondition) return false;

    // Check carry forward doesn't resolve to empty
    if (item.type === "question" && item.carryForwardFrom) {
      const result = OptionsResolverUtils.getDisplayOptions(
        item,
        stateHistory,
        isEditMode
      );

      // Hide question if carry forward results in no options
      // This could happen if the source question has an other text field
      // and the respondent only entered text and did not select an option
      if (result.isCarryForward && result.options.length === 0) {
        return false;
      }
    }

    return true;
  });

  const skipIdentificationCollection =
    isRecruitedParticipant(searchParams) ||
    searchParams[NO_COLLECT_EMAIL_PARAM] === "true";

  if (skipIdentificationCollection) {
    return filteredItems.filter(
      (item) => item.type !== "identification_collection"
    );
  }

  return filteredItems;
};

type ShownTemplateKey = string & { readonly __brand: unique symbol };

const shownTemplateKey = (
  templateId: InterviewItemTemplateId,
  conceptId: ConceptId | null
): ShownTemplateKey =>
  `${templateId}-${conceptId ?? "null"}` as ShownTemplateKey;

const canConditionalEverBeTrueForItem = ({
  conditional,
  conceptId,
  shownItemIds,
  shownTemplateKeys,
}: {
  conditional: ConditionalDef;
  conceptId: ConceptId | null;
  shownItemIds: Set<InterviewItemId>;
  shownTemplateKeys: Set<ShownTemplateKey>;
}): boolean => {
  const hasShownTemplateForConcept = (
    templateId: InterviewItemTemplateId
  ): boolean =>
    shownTemplateKeys.has(shownTemplateKey(templateId, null)) ||
    (conceptId !== null &&
      shownTemplateKeys.has(shownTemplateKey(templateId, conceptId)));

  switch (conditional.type) {
    case "conjunction":
      return conditional.items.every((item) =>
        canConditionalEverBeTrueForItem({
          conditional: item,
          conceptId,
          shownItemIds,
          shownTemplateKeys,
        })
      );
    case "disjunction":
      return conditional.items.some((item) =>
        canConditionalEverBeTrueForItem({
          conditional: item,
          conceptId,
          shownItemIds,
          shownTemplateKeys,
        })
      );
    case "concept":
      return conceptId !== null && conditional.concepts.includes(conceptId);
    case "searchParam":
    case "target_group":
    case "audience":
    case "segment":
      return true;
    case "selectedTemplateOptions":
      switch (conditional.matchingCriteria) {
        case "mustSelect":
        case "mustSelectAtLeast":
          return hasShownTemplateForConcept(conditional.templateId);
        case "mustNotSelect":
          return true;
        default:
          return assertNever(conditional);
      }
    case "selectedOptions":
      switch (conditional.matchingCriteria) {
        case "mustSelect":
          return shownItemIds.has(conditional.itemId);
        case "mustNotSelect":
          return true;
        default:
          return assertNever(conditional);
      }
    case "answerString": {
      const effectiveMatchingCriteria = conditional.matchingCriteria ?? "exact";
      switch (effectiveMatchingCriteria) {
        case "exact":
        case "contains":
          return shownItemIds.has(conditional.itemId);
        case "notEquals":
          return true;
        default:
          return assertNever(effectiveMatchingCriteria);
      }
    }
    default:
      assertNever(conditional);
  }
};

/**
 * Filters to the subset of interview items that can ever be shown at runtime.
 * This runs as a single forward pass over the ordered interview items, using
 * previously-shown items/templates as the DP state for later conditionals.
 *
 * This is intentionally conservative for runtime-dependent conditionals:
 * - search params / target groups / segments are assumed satisfiable
 * - conditionals on reachable prior questions are assumed satisfiable
 *
 * But it does prune items whose conditionals can never pass because the
 * prerequisite question itself can never be shown. This matches the empty-state
 * semantics in `passesConditional`, so `mustNotSelect` / `notEquals`
 * conditionals can still remain reachable even when the referenced question is
 * never shown.
 */
export const filterItemsThatCanEverBeShown = (
  items: InterviewItem[]
): InterviewItem[] => {
  const shownItemIds = new Set<InterviewItemId>();
  const shownTemplateKeys = new Set<ShownTemplateKey>();

  return items.filter((item) => {
    const canEverBeShown =
      !item.conditional ||
      canConditionalEverBeTrueForItem({
        conditional: item.conditional,
        conceptId: item.conceptId,
        shownItemIds,
        shownTemplateKeys,
      });

    if (canEverBeShown) {
      shownItemIds.add(item.id);
      shownTemplateKeys.add(shownTemplateKey(item.templateId, item.conceptId));
    }

    return canEverBeShown;
  });
};

export const responsePassesConditional = ({
  aspect,
  response,
  interviewItems,
}: {
  aspect: Aspect;
  response: Response | ResponseWithRev;
  interviewItems: InterviewItem[];
}) => {
  const aspectQuestion = interviewItems.find(
    (q) =>
      q.templateId === aspect.item_template_id &&
      q.conceptId === aspect.concept_id
  );
  if (!aspectQuestion) return true;
  return passesConditional({
    conditional: aspectQuestion.conditional,
    searchParams: response.data.searchParams,
    stateHistory: response.data.history,
    items: interviewItems,
    additionalData: response.data.additionalData,
    conceptId: aspectQuestion.conceptId,
  });
};

export const shouldScreenOutInterview = (
  interviewItems: InterviewItem[],
  history: QAState[],
  screeningCriteria: ScreenerQuestionRule[],
  historyIndex: number
): ScreenerQuestionRule | null => {
  // slice is exclusive of the end index
  const relevantQuestions = history.slice(0, historyIndex + 1);

  const relevantCriteria = screeningCriteria.filter((q) =>
    shouldScreenOutQuestion(interviewItems, relevantQuestions, q)
  );
  return relevantCriteria[0] ?? null;
};

export const shouldScreenOutQuestion = (
  interviewItems: InterviewItem[],
  history: QAState[],
  screeningCriteria: ScreenerQuestionRule
) => {
  const relevantState = history.find(
    (s) =>
      s.currentTemplateId === screeningCriteria.interview_question_item &&
      s.selectedOptionIds.length > 0
  );
  const relevantItem = interviewItems.find(
    (i) => i.templateId === screeningCriteria.interview_question_item
  );
  if (!relevantState || !relevantItem) return null;
  const selectedReject = screeningCriteria.rejectOptions.some((r) =>
    relevantState.selectedOptionIds.includes(r)
  );
  const selectedApprove = screeningCriteria.approveOptions.some((r) =>
    relevantState.selectedOptionIds.includes(r)
  );
  const noApproveOptions = screeningCriteria.approveOptions.length === 0;
  if (!selectedReject && (selectedApprove || noApproveOptions)) {
    return null;
  }
  return screeningCriteria;
};

export const stringifyConcepts = (concepts: Concept[]) => {
  const getTitle = (c: Concept) =>
    c.content.title ? `<title>${c.content.title}</title>` : "";
  const getDescription = (c: Concept) =>
    c.content.description
      ? `<description>${c.content.description}</description>`
      : "";
  return concepts
    .map(
      (c) =>
        `<concept name=${c.nickname}>${getTitle(c)}${getDescription(c)}</concept>`
    )
    .join("\n");
};

// =========== History Integration Helpers ===========

/**
 * Extracts the question ID from an assistant message.
 * Assistant messages end with "####\n[id]"
 */
export const extractQuestionIdFromMessage = (
  content: string
): number | null => {
  // Old \s*\n?\s* was ReDoS; \n is already in \s, so \s* is linear.
  const match = content.match(/####\s*(\d+)\s*$/);
  return match ? parseInt(match[1], 10) : null;
};

/**
 * Extracts the display text from an assistant message (without the #### and ID suffix)
 */
export const extractDisplayText = (content: string): string => {
  return content.replace(/####\s*\d+\s*$/, "").trim();
};

/**
 * Converts history messages to an array of [questionId, numAssistantQuestionsAsked, message] triples.
 * Each message is paired with the question ID it belongs to and the cumulative count of
 * assistant messages for that question ID up to this point.
 * If extractQuestionIdFromMessage returns null, uses the previously extracted ID.
 * If no ID has been extracted yet, defaults to 0.
 */
export const historyToIDHistoryTriples = (
  historyMessages: ChatGPTMessage[]
): Array<[number, number, ChatGPTMessage]> => {
  let currentQuestionId = 0;
  const assistantCountByQuestionId = new Map<number, number>();

  return historyMessages.map((msg) => {
    if (msg.role === "assistant") {
      const extractedId = extractQuestionIdFromMessage(msg.content ?? "");
      if (extractedId !== null) {
        currentQuestionId = extractedId;
      }
      // Increment the assistant count for this question ID
      const currentCount =
        (assistantCountByQuestionId.get(currentQuestionId) ?? 0) + 1;
      assistantCountByQuestionId.set(currentQuestionId, currentCount);
    }

    const numAssistantQuestions =
      assistantCountByQuestionId.get(currentQuestionId) ?? 0;
    return [currentQuestionId, numAssistantQuestions, msg];
  });
};

/** Readable transcript for LangSmith eval traces; strips `####<id>` markers, tags assistant turns by question id. */
export const renderConversationTranscript = (
  messages: ChatGPTMessage[]
): string =>
  historyToIDHistoryTriples(messages)
    .map(([questionId, , msg]) => {
      const isAssistant = msg.role === "assistant";
      const header = isAssistant
        ? `── assistant (question_id=${questionId}) ──`
        : `── ${msg.role} ──`;
      const raw = msg.content ?? "";
      const text =
        (isAssistant ? extractDisplayText(raw) : raw.trim()) || "(no text)";
      return `${header}\n${text}`;
    })
    .join("\n\n");

/**
 * Groups conversation history by question ID.
 * Returns a map where each key is a question index and the value is an array of conversation turns.
 */
export const groupHistoryByQuestionId = (
  historyMessages: ChatGPTMessage[]
): Map<
  number,
  Array<{ role: "assistant" | "user"; content: string; raw_content: string }>
> => {
  const conversationByQuestionId: Map<
    number,
    Array<{ role: "assistant" | "user"; content: string; raw_content: string }>
  > = new Map();

  let currentQuestionId: number | null = null;
  for (const msg of historyMessages) {
    const content = msg.content ?? "";
    if (msg.role === "assistant") {
      const questionId = extractQuestionIdFromMessage(content);
      if (questionId !== null) {
        currentQuestionId = questionId;
      }
      // Add the assistant message to the current question's conversation
      if (currentQuestionId !== null) {
        if (!conversationByQuestionId.has(currentQuestionId)) {
          conversationByQuestionId.set(currentQuestionId, []);
        }
        conversationByQuestionId.get(currentQuestionId)!.push({
          role: "assistant",
          content: extractDisplayText(content),
          raw_content: content,
        });
      }
    } else if (msg.role === "user" && currentQuestionId !== null) {
      // Add user response to the current question's conversation
      conversationByQuestionId.get(currentQuestionId)!.push({
        role: "user",
        content,
        raw_content: content,
      });
    }
  }

  return conversationByQuestionId;
};

// =========== Item Stringification ===========

/**
 * Wraps stringifyInterviewItems with a <{label}>...</{label}> wrapper.
 * Optionally integrates conversation history with items.
 *
 * @param items - The interview items to stringify
 * @param startIndex - The index to start from (inclusive)
 * @param endIndex - The index to end at (exclusive)
 * @param searchParams - Search params for media filtering and message formatting
 * @param label - The label to use for the wrapper e.g. past_items, current_item, next_item, future_items
 * @param options - Additional options
 *   - additionalString: Additional string to add at the start of the wrapper
 *   - labelDescription: Description of the label (shows as info attribute)
 *   - history: If provided, conversation history will be integrated with items
 * @returns Wrapped string in format:
 *   <{label} info="{labelDescription}">
 *     {additionalString}
 *     ...{stringified items with optional conversation history}...
 *   </{label}>
 */
export const stringifySlice = (
  items: InterviewItem[],
  startIndex: number,
  endIndex: number, // exclusive
  searchParams: { [key: string]: string },
  label: string,
  options: {
    additionalString?: string;
    labelDescription?: string;
    history?: ChatGPTMessage[];
    includeAnswerChoices?: boolean;
  } = {}
): string => {
  if (items.length === 0 || endIndex <= startIndex) {
    return "";
  }

  const str = format_message(
    stringifyInterviewItems(
      // important to not slice from startIndex to make sure the index outputted is correct
      items.slice(0, endIndex),
      searchParams,
      {
        skipFirst: startIndex,
        history: options.history,
        includeAnswerChoices: options.includeAnswerChoices,
      }
    ),
    searchParams
  );

  const additionalStr = options.additionalString ?? "";
  const infoAttr = options.labelDescription
    ? ` info="${options.labelDescription}"`
    : "";

  return `<${label}${infoAttr}>
${additionalStr}${str}
</${label}>`;
};

/**
 * Stringify a single interview item.
 * @param item - The interview item to stringify
 * @param index - The index of the item (used as the id)
 * @param searchParams - Search params for media filtering
 * @param options - Additional options
 *  - includeInterviewDetails: Whether to include interview details (e.g. follow_up, type, etc.)
 *  - concept: If provided, concept=concept.nickname will be added to the item
 *  - innerContent: Additional content to include inside the tag (before closing tag), e.g. conversation history
 *  - includeAnswerChoices: If true, for questions with choices render <answer_choices><li>...</li></answer_choices> inside the question tag
 * @returns e.g. some example outputs:
 *  "<question id=2 concept='Fire Pits' follow_up='on short answers'>Tell me about where your fire pit usually lives. Why did you choose that location?</question>"
 *  "<statement id=1 concept='Fire Pits'>Imagine that you have a fire pit in your backyard.</statement>"
 *  "<interview_end id=3 redirect='true'>Thanks! .....</interview_end>"
 */
export const stringifySingleInterviewItem = (
  item: InterviewItem,
  index: number,
  searchParams: { [key: string]: string },
  options: {
    includeInterviewDetails?: boolean;
    concept?: Concept;
    innerContent?: string;
    includeAnswerChoices?: boolean;
  } = {}
): string => {
  const {
    includeInterviewDetails = true,
    concept,
    innerContent,
    includeAnswerChoices = false,
  } = options;
  const conceptStr = concept ? ` concept="${concept.nickname}"` : "";
  const mediaStr = item.media?.length
    ? ` shownMedia=${JSON.stringify(
        filterMediaByParams(item.media, searchParams).map((m) => m.name)
      )}`
    : "";

  const inner = innerContent ? `\n${innerContent}\n` : "";

  if (item.type === "statement") {
    return `<statement id=${index}${conceptStr}${mediaStr}>${item.text}${inner}</statement>`;
  } else if (item.type === "interview_end") {
    const redirDataInstr =
      item.redirect_url && item.redirect_data_def
        ? `After your message and question index, output #### and a json object with the following parameters. It is crucial that it is valid json!
${item.redirect_data_def}`
        : "";
    return `<interview_end id=${index} redirect="${
      item.redirect_url ? "true" : "false"
    }">${item.text}\n${redirDataInstr}${inner}</interview_end>`;
  } else {
    let qStr = `<question id=${index}`;
    qStr += conceptStr;
    if (includeInterviewDetails) {
      if (isRanking(item)) {
        qStr += ` type="ranking"`;
        qStr += ` follow_up="NO FOLLOW-UP QUESTIONS ALLOWED"`;
      } else if (isMultipleChoice(item) || isScale(item)) {
        qStr += ` type="multiple_choice"`;
        qStr += ` follow_up="NO FOLLOW-UP QUESTIONS ALLOWED"`;
      } else if (isMatrix(item)) {
        qStr += ` type="${isCardSort(item) ? "card_sort" : "matrix"}"`;
        qStr += ` follow_up="NO FOLLOW-UP QUESTIONS ALLOWED"`;
      } else if (isMaxDiff(item) || isGaborGranger(item)) {
        // A follow-up turn on these carries no screen or rung, so the interviewer is refused one either way.
        qStr += ` follow_up="NO FOLLOW-UP QUESTIONS ALLOWED"`;
      } else if (item.followUp === "light")
        qStr += ' follow_up="on short answers"';
      else if (item.followUp === "medium")
        qStr += ' follow_up="1 follow-up question"';
      else if (item.followUp === "heavy")
        qStr += ' follow_up="up to 3 follow-up questions"';
      else qStr += ` follow_up="none"`;

      qStr += mediaStr;
    }
    const instructions = [
      item.addInstructions,
      includeInterviewDetails && isMatrix(item)
        ? isCardSort(item)
          ? CARD_SORT_INTERVIEWER_INSTRUCTION
          : MATRIX_INTERVIEWER_INSTRUCTION
        : undefined,
    ]
      .filter(notNull)
      .filter((instruction) => instruction.length > 0)
      .join("\n");
    if (instructions.length > 0)
      qStr += ` instructions=${JSON.stringify(instructions)}`;

    qStr += ">";
    qStr += item.text;
    if (includeAnswerChoices && (item.choices?.length ?? 0) > 0) {
      qStr +=
        "\n<answer_choices>\n" +
        item.choices!.map((c) => `<li>${c.text}</li>`).join("\n") +
        "\n</answer_choices>";
    }
    qStr += inner;
    qStr += "</question>";
    return qStr;
  }
};

/**
 * Stringify interview items, optionally integrating conversation history.
 *
 * @param interviewItems - The interview items to stringify
 * @param searchParams - The search params to use for media filtering
 * @param options - Additional options
 *   - includeInterviewDetails: Whether to include follow_up rules, types, etc. (default: true)
 *   - skipFirst: Number of items to skip from the beginning (default: 0)
 *   - concepts: If provided, concept=nickname will be added to the items
 *   - history: If provided, conversation turns will be integrated with each item
 *   - includeAnswerChoices: If true, questions with choices get <answer_choices><li>...</li></answer_choices> inside the question tag
 * @returns e.g. some example outputs:
 *  Without history:
 *    "<statement id=1 concept='Fire Pits'>Imagine that you have a fire pit in your backyard.</statement>
 *     <question id=2 concept='Fire Pits' follow_up='on short answers'>Tell me about the fire pit.</question>"
 *
 *  With history:
 *    "<statement id=0 concept='Fire Pits'>Imagine that you have a fire pit in your backyard.</statement>
 *     <question id=1 follow_up='heavy'>What do you like about it?
 *       <interviewer>What do you like about it?</interviewer>
 *       <respondent>I love the design and materials.</respondent>
 *       <interviewer>Ok! What about the design do you like?</interviewer>
 *     </question>"
 */
export const stringifyInterviewItems = (
  interviewItems: InterviewItem[],
  searchParams: { [key: string]: string },
  {
    includeInterviewDetails = true,
    skipFirst = 0,
    concepts,
    history,
    includeAnswerChoices = false,
  }: {
    includeInterviewDetails?: boolean;
    skipFirst?: number;
    // If provided, concept=nickname will be added to the items
    concepts?: Concept[];
    // If provided, conversation history will be integrated with items
    history?: ChatGPTMessage[];
    includeAnswerChoices?: boolean;
  } = {}
) => {
  // If history is provided, use integrated format with conversations
  if (history && history.length > 0) {
    const conversationByQuestionId = groupHistoryByQuestionId(history);
    const parts: string[] = [];

    for (let i = skipFirst; i < interviewItems.length; i++) {
      const item = interviewItems[i];
      const concept = concepts?.find((c) => c.id === item.conceptId);

      const conversation = conversationByQuestionId.get(i);
      // If history is provided, this will be a defined string and passed
      // to stringifySingleInterviewItem as innerContent
      const conversationStr =
        conversation && conversation.length > 0
          ? conversation
              .map((turn) =>
                turn.role === "assistant"
                  ? // Display raw content with the #### and ID suffix
                    `<interviewer>\n${turn.raw_content}\n</interviewer>`
                  : `<respondent>\n${turn.content}\n</respondent>`
              )
              .join("\n")
          : undefined;

      const itemStr = stringifySingleInterviewItem(item, i, searchParams, {
        includeInterviewDetails,
        concept,
        innerContent: conversationStr,
        includeAnswerChoices,
      });

      parts.push(itemStr);
    }

    return parts.join("\n\n");
  }

  // Simple format without history (existing behavior)
  const stringifiedItems = interviewItems
    .map((item, index) => {
      const concept = concepts?.find((c) => c.id === item.conceptId);
      return stringifySingleInterviewItem(item, index, searchParams, {
        includeInterviewDetails,
        concept,
        includeAnswerChoices,
      });
    })
    .slice(skipFirst)
    .join("\n");

  return stringifiedItems;
};

export const stringifyInterviewGuide = (
  data: SurveyData,
  indOffset = 0,
  options: {
    allConcepts?: boolean;
    concepts?: Concept[];
    includeAnswerChoices?: boolean;
  } = {}
) => {
  const {
    allConcepts = false,
    concepts,
    includeAnswerChoices = false,
  } = options;
  const sampledData = sampleSurveyData(data, {
    onlyTakeConcept: allConcepts ? null : "first",
    includeAllConcepts: allConcepts,
  });
  const stringifiedItems = `<questions>${stringifyInterviewItems(
    sampledData.allInterviewItems,
    {},
    {
      skipFirst: indOffset,
      includeInterviewDetails: false,
      concepts,
      includeAnswerChoices,
    }
  )}</questions>`;

  const stringifiedConcepts =
    concepts && concepts.length > 0
      ? `\n<concepts>${stringifyConcepts(concepts)}</concepts>`
      : "";
  const stringifiedBackground = `<background>${data.background.trim() === "" ? "(No background specified)" : data.background.trim()}</background>`;
  const stringifiedTitle = `<title>${data.title}</title>`;
  const stringifiedGoal = `<study_goal>${data.study_goal?.trim()?.length ? data.study_goal : "(No study goal specified)"}</study_goal>`;

  return `${stringifiedTitle}
${stringifiedGoal}
${stringifiedBackground}${stringifiedConcepts}
${stringifiedItems}
`;
};

export const getWelcomeMessage = (
  interviewItems: InterviewItem[],
  welcomeAndDisclosure?: WelcomeAndDisclosure
) => {
  if (welcomeAndDisclosure?.enabled) {
    return welcomeAndDisclosure.message;
  }
  return (
    interviewItems[0]?.text ??
    "To start, please add a question to the interview guide."
  );
};

/**
 * Checks if a question is a scale question (e.g., Likert scales, 1-5 ratings).
 * Scale questions have ordered numeric values.
 *
 * Note: Scale questions are not actively in use in the product, but may be down the line.
 *
 * Use this when you need scale-specific logic.
 */
export const isScale = (q: InterviewItem | InterviewItemTemplate | undefined) =>
  notNull(q) && q.type === "question" && q.questionType === "scale";

/**
 * Checks if a question has structured options (multiple choice, scale, or ranking).
 * These question types share common characteristics:
 * - They all have predefined options/choices
 * - They don't need LLM follow-up questions
 * - They use button/option-based UI instead of text input
 * - They're faster to answer than open-ended questions
 */
export const isStructuredQuestion = (
  q: InterviewItem | InterviewItemTemplate | undefined
) =>
  notNull(q) &&
  q.type === "question" &&
  (q.questionType === "multiple_choice" ||
    q.questionType === "scale" ||
    q.questionType === "ranking" ||
    q.questionType === "matrix" ||
    q.questionType === "max_diff" ||
    q.questionType === "conjoint" ||
    q.questionType === "gabor_granger");

/**
 * Question types that have their own dedicated analysis pipeline
 * (response_ranking_entries / matrix-stats aggregation / response_max_diff_entries
 * + HB fit / response_gabor_granger_entries) and therefore should not get theme
 * generation or a Themes column in the response table. Single source of
 * truth for this invariant; consumed by `find_themes.ts` and the table
 * column gate in `columnVisibility.ts`.
 */
export const hasDedicatedAnalysisPipeline = (
  q: Pick<InterviewItemTemplate, "type" | "questionType"> | undefined | null
) =>
  isRanking(q) ||
  isMatrix(q) ||
  isMaxDiff(q) ||
  isGaborGranger(q) ||
  isConjoint(q);

/**
 * Checks ONLY for multiple_choice questions (not scale or ranking).
 *
 * Multiple choice questions are distinct from scale/ranking:
 * - They have categorical options (not ordered/numeric like scale)
 * - They can be single-select or multi-select
 * - They need theme generation (scale/ranking don't)
 * - Single-select MC should be classified as "category" data type for analysis
 *
 */
export const isMultipleChoice = (
  q: Pick<InterviewItemTemplate, "type" | "questionType"> | undefined | null
) =>
  notNull(q) && q.type === "question" && q.questionType === "multiple_choice";

export const isRanking = (
  q: Pick<InterviewItemTemplate, "type" | "questionType"> | undefined | null
) => notNull(q) && q.type === "question" && q.questionType === "ranking";

export const isMatrix = (
  q: Pick<InterviewItemTemplate, "type" | "questionType"> | undefined | null
) => notNull(q) && q.type === "question" && q.questionType === "matrix";

/**
 * A card sort is a matrix drawn as drag-and-drop, rows as cards and columns as
 * groups. Every surface telling them apart goes through this, so open sort changes one shape.
 */
export const isCardSort = (
  q:
    | Pick<InterviewItemTemplate, "type" | "questionType" | "matrixDisplay">
    | undefined
    | null
) => isMatrix(q) && q?.matrixDisplay === "card_sort";

export const isMaxDiff = (
  q: Pick<InterviewItemTemplate, "type" | "questionType"> | undefined | null
) => notNull(q) && q.type === "question" && q.questionType === "max_diff";

export const isConjoint = (
  q: Pick<InterviewItemTemplate, "type" | "questionType"> | undefined | null
) => notNull(q) && q.type === "question" && q.questionType === "conjoint";
export const isGaborGranger = (
  q: Pick<InterviewItemTemplate, "type" | "questionType"> | undefined | null
) => notNull(q) && q.type === "question" && q.questionType === "gabor_granger";

export const isTask = <
  T extends Pick<InterviewItemTemplate, "type" | "questionType" | "task">,
>(
  q: T | undefined | null
): q is T & { task: TaskConfig } =>
  notNull(q) &&
  q.type === "question" &&
  q.questionType === "open_ended" &&
  notNull(q.task);

// Selection mode helpers for multi-select questions
export type SelectionMode = "single" | "unlimited" | "exact" | "range";

export const getSelectionMode = (item: {
  multi_select?: boolean;
  min_select?: number;
  max_select?: number;
}): SelectionMode => {
  if (!item.multi_select) return "single";
  if (item.max_select === undefined) return "unlimited";
  if (item.min_select === item.max_select) return "exact";
  return "range";
};

export const isSingleSelect = (item: { multi_select?: boolean }): boolean =>
  !item.multi_select;

export const isUnlimitedSelect = (item: {
  multi_select?: boolean;
  max_select?: number;
}): boolean => item.multi_select === true && item.max_select === undefined;

export const isExactSelect = (item: {
  multi_select?: boolean;
  min_select?: number;
  max_select?: number;
}): boolean =>
  item.multi_select === true &&
  item.max_select !== undefined &&
  item.min_select === item.max_select;

export const isRangeSelect = (item: {
  multi_select?: boolean;
  min_select?: number;
  max_select?: number;
}): boolean =>
  item.multi_select === true &&
  item.max_select !== undefined &&
  item.min_select !== item.max_select;

export const isOpenEnded = (
  q: Pick<InterviewItemTemplate, "type" | "questionType"> | undefined | null
) => notNull(q) && q.type === "question" && q.questionType === "open_ended";

// The editor keeps follow-up guidelines stored (but hidden) when follow-ups are
// switched off, so they survive toggling. The moderator must not see them then.
export const getActiveAddInstructions = (
  q: Pick<QuestionItem, "questionType" | "followUp" | "addInstructions">
): string | undefined => {
  const isOpenEndedWithFollowUpsOff =
    q.questionType === "open_ended" && (q.followUp ?? "none") === "none";
  return isOpenEndedWithFollowUpsOff ? undefined : q.addInstructions;
};

export const isFileUpload = (
  q: Pick<InterviewItemTemplate, "type" | "questionType"> | undefined | null
) => notNull(q) && q.type === "question" && q.questionType === "file_upload";

export const surveyHasFileUploadQuestions = (surveyData: SurveyData): boolean =>
  countFileUploadQuestions(surveyData) > 0;

export const countFileUploadQuestions = (surveyData: SurveyData): number =>
  sampleSurveyData(surveyData, {
    includeAllConcepts: true,
  }).allInterviewItems.filter(isFileUpload).length;

export const surveyRevisionsHaveFileUploadQuestions = (
  revisions: Pick<SurveyRevision, "data">[]
): boolean => revisions.some((rev) => surveyHasFileUploadQuestions(rev.data));

export const get_question_weight_time_estimate = (item: InterviewItem) => {
  let weight = 1;
  if (isOpenEnded(item)) {
    if (item.followUp === "light") weight += 1;
    if (item.followUp === "medium") weight += 1.5;
    if (item.followUp === "heavy") weight += 2;
  }
  return weight;
};

export const questionWeight = (item: InterviewItem | undefined) => {
  const weightMultipleChoice = 0.5; // also for statements
  const weightOpenEnded = 1;
  const addWeightLight = 0.75;
  const addWeightMedium = 1.125;
  const addWeightHeavy = 1.5;

  if (!item) return 0;
  if (item.type === "unsampled_concept_block") {
    if (!item.progressWeight) {
      console.error(
        "questionWeight: no progress weight for unsampled concept block",
        item
      );
      return 0;
    }
    return item.progressWeight;
  }

  if (isMaxDiff(item)) {
    // A max diff question expands into multiple screens (one per choice subset).
    // Each screen contributes one structured-question worth of weight so the
    // progress bar advances per screen.
    return weightMultipleChoice * getMaxDiffTotalScreens(item);
  }

  if (isConjoint(item)) {
    // Same shape as max diff: one screen (and one QAState) per choice task, so
    // weigh the question by its task count instead of as a single item.
    return weightMultipleChoice * getConjointTotalTasks(item);
  }

  if (isGaborGranger(item)) {
    // A price ladder expands into one rung per price shown. The exact count is
    // per respondent (the bracket closes as soon as their switch point is
    // pinned), so budget the same midpoint estimate the time estimate uses.
    return weightMultipleChoice * getGaborGrangerEstimatedRungs(item);
  }

  if (isStructuredQuestion(item) || item.type === "statement") {
    return weightMultipleChoice;
  } else if (item.type === "question") {
    let w = weightOpenEnded;
    if (item.followUp === "light") w += addWeightLight;
    if (item.followUp === "medium") w += addWeightMedium;
    if (item.followUp === "heavy") w += addWeightHeavy;
    return w;
  } else {
    // type interview_end
    return 0;
  }
};

export const getProgress = (
  items: InterviewItem[],
  stateHistory: QAState[]
) => {
  // if no state history, the interview is still in screening so progress should be 0
  if (stateHistory.length === 0) return 0;

  const startWeight = 0.1;
  const endingWeight = 0.1;
  const weightPerFollowup = 0.5;

  const sumProgress = (qs: InterviewItem[]) =>
    qs.reduce((acc, q) => acc + questionWeight(q), startWeight);

  const finalWeight = sumProgress(items) + endingWeight;

  const indexByItem = new Map(items.map((i, idx) => [i.id, idx]));
  const currentIndex = indexByItem.get(
    stateHistory[stateHistory.length - 1].currentItemId
  )!;
  const currentItem = items[currentIndex];
  if (!currentItem) {
    // should not happen
    console.log("getProgress: no current item (index)", currentIndex);
    return 0;
  }

  const numMesgThisQuestion = stateHistory.filter(
    (s) => s.currentItemId === currentItem.id
  ).length;

  const completedQuestions = items.filter(
    (item) => indexByItem.get(item.id)! < currentIndex
  );

  const curAddWeight = Math.min(
    (numMesgThisQuestion - 1) * weightPerFollowup,
    questionWeight(currentItem)
  );

  const curWeight =
    currentItem.type === "interview_end"
      ? finalWeight
      : sumProgress(completedQuestions) + curAddWeight;
  // console.log({ finalWeight, curWeight, curAddWeight });
  // apply a non-linear function so that the user makes more progress in the beginning
  // and less progress towards the end
  const f = (x: number) => Math.sqrt(x);
  //return (curWeight / finalWeight) * 100;
  return f(curWeight / finalWeight) * 100;
};

export const filterMediaByParams = (
  media: MediaDef[],
  searchParams: { [key: string]: string }
) => {
  /*
  If file name is "[param1=value1,value2;param2=value3]name.end"
  then the file will be shown if the param is equal to value1 or value2 and param2 is equal to value3
  */
  const shownMedia = media.filter((m) => {
    const fileName = m.name;
    if (!fileName.startsWith("[") || fileName.indexOf("]") === -1) return true;

    const end = fileName.indexOf("]");
    const paramsString = fileName.slice(1, end);
    const params = paramsString.split(";");

    return params.every((param) => {
      const [key, values] = param.split("=");
      if (!key || !values) return true;
      const value = searchParams[key];
      // If the search parameter is not present or the value does not match, filter out the media
      return value && values.split(",").includes(value);
    });
  });
  return shownMedia;
};

export const interviewTemplatesById = (blocks: InterviewBlock[]) => {
  return new Map<InterviewItemTemplateId, InterviewItemTemplate>(
    blocks.flatMap((block) => block.items.map((item) => [item.id, item]))
  );
};

export type ReadableQuestionNumbers = {
  templateNumber: number;
  conceptNumber: number | undefined;
  conceptNickname: string | undefined;
  blockNumber: number;
};

export const readableTemplateNumbersByItem = (
  surveyData: SurveyDataSingleLanguage
): Map<InterviewItemTemplateId, number> => {
  return new Map(
    sampleSurveyData(surveyData, {
      onlyTakeConcept: "first",
      // Preview routes may carry translation stubs without blocks; numbering only uses base items.
      include_translations: false,
    }).allInterviewItems.map((item, ind) => [item.templateId, ind + 1])
  );
};

export const readableConceptNumbers = (
  surveyData: SurveyData
): Map<ConceptId, { conceptNumber: number; conceptNickname: string }> => {
  return new Map(
    surveyData.concepts.map((c, ind) => [
      c.id,
      { conceptNumber: ind + 1, conceptNickname: c.nickname },
    ])
  );
};

export const readableBlockNumbers = (
  surveyData: SurveyData
): Map<InterviewBlockId, number> => {
  return new Map(surveyData.blocks.map((b, ind) => [b.id, ind + 1]));
};

export const readableQuestionNumbersByItem = (
  surveyData: SurveyData
): Map<InterviewItemId, ReadableQuestionNumbers> => {
  const templateNumbers = readableTemplateNumbersByItem(surveyData);
  const conceptNumbers = readableConceptNumbers(surveyData);
  const blockNumbers = readableBlockNumbers(surveyData);
  const allItems = sampleSurveyData(surveyData, {
    includeAllConcepts: true,
  }).allInterviewItems;
  return new Map(
    allItems.map((item) => [
      item.id,
      {
        templateNumber: templateNumbers.get(item.templateId)!,
        conceptNumber: item.conceptId
          ? conceptNumbers.get(item.conceptId)?.conceptNumber
          : undefined,
        conceptNickname: item.conceptId
          ? conceptNumbers.get(item.conceptId)?.conceptNickname
          : undefined,
        blockNumber: blockNumbers.get(item.blockId)!,
      },
    ])
  );
};

export const formatQuestionNumber = (
  numbers: ReadableQuestionNumbers | undefined
) => {
  return `${numbers?.templateNumber ?? "end"}${numbers?.conceptNumber ? ` (${numbers.conceptNickname})` : ""}`;
};
